/**
 * Deterministic homestead room recognition.
 *
 * Socket-connected structural components are rasterized into a component-local
 * 0.5 m voxel field. Exterior air is flood-filled from the field shell; air the
 * flood cannot reach is indoors. Doorway gaps are temporarily sealed during the
 * solve (and tagged) so a real, traversable doorway counts as a room boundary
 * rather than letting the exterior flood through it.
 *
 * The result is shared by client and server. It contains compact query fields
 * for indoor tests and, later, deterministic resident navigation; no THREE or
 * platform-specific geometry enters this module.
 */
import { PIECE_CATALOG } from "./pieces.js";
import { pieceWorldObbs, type WorldObb } from "./collision.js";
import { isFurniturePiece, structuralComponents } from "./support.js";
import type { PlacedPiece, Quat, Vec3 } from "./types.js";

export const ROOM_VOXEL_M = 0.5;
export const ROOM_MAX_VOXELS = 500_000;
export const ROOM_SEAM_M = 0.06;
export const ROOM_MIN_FLOOR_M2 = 4;
export const ROOM_MIN_HEADROOM_M = 1.8;

export type RoomFunction =
  | "dwelling"
  | "commons"
  | "mine"
  | "farm"
  | "salvage"
  | "harvester"
  | "workshop"
  | "smithy"
  | "kitchen"
  | "exchange"
  | "office"
  | "storage";

export interface RoomFunctionRecipe {
  function: RoomFunction;
  /** Every listed catalog id must be present in the room. */
  all: readonly string[];
  /** At least one listed id must be present; empty means no any-of clause. */
  any?: readonly string[];
}

/** Data-first room recipes. Exchange's stall-only alternative is handled by
 * the classifier because it has two complete clauses (stall OR counter+shelf). */
export const ROOM_FUNCTION_RECIPES: readonly RoomFunctionRecipe[] = [
  { function: "dwelling", all: ["hs-bed"], any: ["hs-lamp", "hs-hearth"] },
  { function: "commons", all: ["hs-table", "hs-stool"], any: ["hs-lamp", "hs-hearth"] },
  { function: "mine", all: ["hs-mining-rig"] },
  { function: "farm", all: ["hs-farm-plot"] },
  { function: "salvage", all: ["hs-salvage-rig"] },
  { function: "harvester", all: ["hs-harvester"] },
  { function: "workshop", all: ["hs-workbench", "hs-shelf"] },
  { function: "smithy", all: ["hs-forge", "hs-anvil"] },
  { function: "kitchen", all: ["hs-stove", "hs-counter"] },
  { function: "storage", all: ["hs-chest", "hs-shelf"] },
  { function: "office", all: ["hs-admin-desk", "hs-shelf"] },
] as const;

export interface HomesteadRoom {
  /** Lowest recognized function anchor. Newer furniture cannot silently
   * rename an established multi-function room. */
  id: string;
  component: number;
  anchorPid: number | null;
  /** Stable facility identity per recognized function. */
  functionAnchors: Partial<Record<RoomFunction, number>>;
  /** Assignable physical stations per function, sorted by persistent pid. */
  functionSlots: Record<RoomFunction, number[]>;
  centre: Vec3;
  volumeM3: number;
  floorAreaM2: number;
  doorPids: number[];
  furniturePids: number[];
  functions: RoomFunction[];
  primary: RoomFunction | "room";
  /** Count of distinct useful furniture categories, never duplicate quantity. */
  comfort: number;
}

/** Component-local orthonormal frame and compact voxel labels. `roomLabels`
 * stores global room index + 1 (0 = no valid room). `interior` also marks
 * enclosed, usable air that lacks a door and therefore is not a valid room. */
export interface HomesteadRoomField {
  component: number;
  origin: Vec3;
  xAxis: Vec3;
  yAxis: Vec3;
  zAxis: Vec3;
  min: Vec3;
  nx: number;
  ny: number;
  nz: number;
  solid: Uint8Array;
  /** Temporary doorway seal used only by the enclosure flood. Navigation
   * treats these cells as open again. */
  doorways: Uint8Array;
  interior: Uint8Array;
  roomLabels: Int32Array;
}

export interface HomesteadRoomSolution {
  rooms: HomesteadRoom[];
  fields: HomesteadRoomField[];
  /** Socket-components skipped rather than stalling a client/server intent. */
  tooLargeComponents: number[];
}

interface Frame {
  origin: Vec3;
  x: Vec3;
  y: Vec3;
  z: Vec3;
}

const DOOR_ID = "hs-wall-door";
const CELL_PAD = ROOM_VOXEL_M / 2 + ROOM_SEAM_M;
const GRID_MARGIN = ROOM_VOXEL_M * 2;
const HEAD_CELLS = Math.ceil(ROOM_MIN_HEADROOM_M / ROOM_VOXEL_M);
const PRIMARY_ORDER: readonly RoomFunction[] = [
  "mine", "farm", "salvage", "harvester", "smithy", "kitchen", "workshop",
  "exchange", "office", "dwelling", "commons", "storage",
];

/** One station family owns worker bindings for each room function. Supporting
 * furniture licenses the function but does not accidentally create a worker
 * slot (for example a forge does not make its required anvil a second smith). */
export const ROOM_FUNCTION_STATIONS: Readonly<Record<RoomFunction, readonly string[]>> = {
  mine: ["hs-mining-rig"],
  farm: ["hs-farm-plot"],
  salvage: ["hs-salvage-rig"],
  harvester: ["hs-harvester"],
  smithy: ["hs-anvil"],
  kitchen: ["hs-stove"],
  workshop: ["hs-workbench"],
  exchange: ["hs-stall", "hs-counter"],
  office: ["hs-admin-desk"],
  dwelling: ["hs-bed"],
  commons: ["hs-table"],
  storage: ["hs-chest", "hs-shelf"],
};

function emptyFunctionSlots(): Record<RoomFunction, number[]> {
  return {
    dwelling: [], commons: [], mine: [], farm: [], salvage: [], harvester: [],
    workshop: [], smithy: [], kitchen: [], exchange: [], office: [], storage: [],
  };
}

function qRot(q: Quat, v: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q;
  const tx = 2 * (qy * v[2] - qz * v[1]);
  const ty = 2 * (qz * v[0] - qx * v[2]);
  const tz = 2 * (qx * v[1] - qy * v[0]);
  return [
    v[0] + qw * tx + qy * tz - qz * ty,
    v[1] + qw * ty + qz * tx - qx * tz,
    v[2] + qw * tz + qx * ty - qy * tx,
  ];
}

function qInvRot(q: Quat, v: Vec3): Vec3 {
  return qRot([-q[0], -q[1], -q[2], q[3]], v);
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function norm(v: Vec3, fallback: Vec3): Vec3 {
  const n = Math.sqrt(dot(v, v));
  return n > 1e-9 ? [v[0] / n, v[1] / n, v[2] / n] : fallback;
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function frameFor(component: readonly PlacedPiece[]): Frame {
  const root = component[0];
  const radial = norm(root.p, [0, 1, 0]);
  let x = qRot(root.q, [1, 0, 0]);
  // Remove any quaternion drift into radial up, then rebuild z from a cross so
  // the field remains orthonormal even for old/non-normalized stored quats.
  const xu = dot(x, radial);
  x = norm([x[0] - radial[0] * xu, x[1] - radial[1] * xu, x[2] - radial[2] * xu],
    Math.abs(radial[1]) < 0.9 ? norm(cross([0, 1, 0], radial), [1, 0, 0]) : [1, 0, 0]);
  const z = norm(cross(x, radial), [0, 0, 1]);
  return { origin: [...root.p], x, y: radial, z };
}

function toFrame(frame: Frame, p: Vec3): Vec3 {
  const d = sub(p, frame.origin);
  return [dot(d, frame.x), dot(d, frame.y), dot(d, frame.z)];
}

function toWorld(frame: Frame, p: Vec3): Vec3 {
  return [
    frame.origin[0] + frame.x[0] * p[0] + frame.y[0] * p[1] + frame.z[0] * p[2],
    frame.origin[1] + frame.x[1] * p[0] + frame.y[1] * p[1] + frame.z[1] * p[2],
    frame.origin[2] + frame.x[2] * p[0] + frame.y[2] * p[1] + frame.z[2] * p[2],
  ];
}

function obbExtentInFrame(frame: Frame, b: WorldObb): Vec3 {
  const ax = qRot(b.q, [1, 0, 0]);
  const ay = qRot(b.q, [0, 1, 0]);
  const az = qRot(b.q, [0, 0, 1]);
  return [frame.x, frame.y, frame.z].map((f) =>
    Math.abs(dot(f, ax)) * b.h[0]
    + Math.abs(dot(f, ay)) * b.h[1]
    + Math.abs(dot(f, az)) * b.h[2]) as Vec3;
}

function doorwayObb(piece: PlacedPiece): WorldObb {
  const local: Vec3 = [0, 1.05, 0];
  const d = qRot(piece.q, local);
  const h: Vec3 = [0.6, 1.05, 0.1];
  return {
    pid: piece.pid,
    c: [piece.p[0] + d[0], piece.p[1] + d[1], piece.p[2] + d[2]],
    q: piece.q,
    h,
    r: Math.sqrt(h[0] * h[0] + h[1] * h[1] + h[2] * h[2]),
  };
}

function idx(field: Pick<HomesteadRoomField, "nx" | "ny">, x: number, y: number, z: number): number {
  return x + field.nx * (y + field.ny * z);
}

function cellLocal(field: HomesteadRoomField, x: number, y: number, z: number): Vec3 {
  return [
    field.min[0] + (x + 0.5) * ROOM_VOXEL_M,
    field.min[1] + (y + 0.5) * ROOM_VOXEL_M,
    field.min[2] + (z + 0.5) * ROOM_VOXEL_M,
  ];
}

function pointIndex(field: HomesteadRoomField, p: Vec3): number {
  const frame: Frame = {
    origin: field.origin, x: field.xAxis, y: field.yAxis, z: field.zAxis,
  };
  const q = toFrame(frame, p);
  const x = Math.floor((q[0] - field.min[0]) / ROOM_VOXEL_M);
  const y = Math.floor((q[1] - field.min[1]) / ROOM_VOXEL_M);
  const z = Math.floor((q[2] - field.min[2]) / ROOM_VOXEL_M);
  if (x < 0 || y < 0 || z < 0 || x >= field.nx || y >= field.ny || z >= field.nz) return -1;
  return idx(field, x, y, z);
}

function rasterize(field: HomesteadRoomField, frame: Frame, b: WorldObb, door: boolean,
  doorMask: Uint8Array): void {
  const c = toFrame(frame, b.c);
  const e = obbExtentInFrame(frame, b);
  const x0 = Math.max(0, Math.floor((c[0] - e[0] - CELL_PAD - field.min[0]) / ROOM_VOXEL_M));
  const y0 = Math.max(0, Math.floor((c[1] - e[1] - CELL_PAD - field.min[1]) / ROOM_VOXEL_M));
  const z0 = Math.max(0, Math.floor((c[2] - e[2] - CELL_PAD - field.min[2]) / ROOM_VOXEL_M));
  const x1 = Math.min(field.nx - 1, Math.floor((c[0] + e[0] + CELL_PAD - field.min[0]) / ROOM_VOXEL_M));
  const y1 = Math.min(field.ny - 1, Math.floor((c[1] + e[1] + CELL_PAD - field.min[1]) / ROOM_VOXEL_M));
  const z1 = Math.min(field.nz - 1, Math.floor((c[2] + e[2] + CELL_PAD - field.min[2]) / ROOM_VOXEL_M));
  for (let z = z0; z <= z1; z++) for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
    const world = toWorld(frame, cellLocal(field, x, y, z));
    const p = qInvRot(b.q, sub(world, b.c));
    if (Math.abs(p[0]) > b.h[0] + CELL_PAD
      || Math.abs(p[1]) > b.h[1] + CELL_PAD
      || Math.abs(p[2]) > b.h[2] + CELL_PAD) continue;
    const i = idx(field, x, y, z);
    field.solid[i] = 1;
    if (door) doorMask[i] = 1;
  }
}

function visitNeighbours(field: HomesteadRoomField, i: number, fn: (next: number) => void): void {
  const x = i % field.nx;
  const yz = (i - x) / field.nx;
  const y = yz % field.ny;
  const z = (yz - y) / field.ny;
  if (x > 0) fn(i - 1);
  if (x + 1 < field.nx) fn(i + 1);
  if (y > 0) fn(i - field.nx);
  if (y + 1 < field.ny) fn(i + field.nx);
  const slab = field.nx * field.ny;
  if (z > 0) fn(i - slab);
  if (z + 1 < field.nz) fn(i + slab);
}

function floodExterior(field: HomesteadRoomField): Uint8Array {
  const seen = new Uint8Array(field.solid.length);
  const queue = new Int32Array(field.solid.length);
  let head = 0, tail = 0;
  const seed = (i: number): void => {
    if (field.solid[i] || seen[i]) return;
    seen[i] = 1;
    queue[tail++] = i;
  };
  for (let z = 0; z < field.nz; z++) for (let y = 0; y < field.ny; y++) {
    seed(idx(field, 0, y, z)); seed(idx(field, field.nx - 1, y, z));
  }
  for (let z = 0; z < field.nz; z++) for (let x = 0; x < field.nx; x++) {
    seed(idx(field, x, 0, z)); seed(idx(field, x, field.ny - 1, z));
  }
  for (let y = 0; y < field.ny; y++) for (let x = 0; x < field.nx; x++) {
    seed(idx(field, x, y, 0)); seed(idx(field, x, y, field.nz - 1));
  }
  while (head < tail) {
    const here = queue[head++];
    visitNeighbours(field, here, (next) => {
      if (field.solid[next] || seen[next]) return;
      seen[next] = 1;
      queue[tail++] = next;
    });
  }
  return seen;
}

function furnitureCategories(ids: ReadonlySet<string>): number {
  const categories = new Set<string>();
  const add = (category: string, members: readonly string[]): void => {
    if (members.some((id) => ids.has(id))) categories.add(category);
  };
  add("sleep", ["hs-bed"]);
  add("seating", ["hs-stool"]);
  add("surface", ["hs-table", "hs-counter", "hs-workbench"]);
  add("light", ["hs-lamp", "hs-hearth", "hs-forge", "hs-stove"]);
  add("storage", ["hs-shelf", "hs-chest"]);
  add("heat", ["hs-hearth", "hs-forge", "hs-stove"]);
  add("craft", ["hs-workbench", "hs-forge", "hs-anvil"]);
  add("extract", ["hs-mining-rig", "hs-farm-plot", "hs-salvage-rig", "hs-harvester"]);
  add("trade", ["hs-stall"]);
  add("admin", ["hs-admin-desk"]);
  return categories.size;
}

function classify(ids: ReadonlySet<string>): RoomFunction[] {
  const out: RoomFunction[] = [];
  for (const recipe of ROOM_FUNCTION_RECIPES) {
    if (!recipe.all.every((id) => ids.has(id))) continue;
    if (recipe.any && !recipe.any.some((id) => ids.has(id))) continue;
    out.push(recipe.function);
  }
  if (ids.has("hs-stall") || (ids.has("hs-counter") && ids.has("hs-shelf"))) out.push("exchange");
  return PRIMARY_ORDER.filter((f) => out.includes(f));
}

function functionStations(functions: readonly RoomFunction[], furniture: readonly PlacedPiece[]): {
  anchors: Partial<Record<RoomFunction, number>>;
  slots: Record<RoomFunction, number[]>;
} {
  const anchors: Partial<Record<RoomFunction, number>> = {};
  const slots = emptyFunctionSlots();
  for (const fn of functions) {
    const accepted = new Set(ROOM_FUNCTION_STATIONS[fn]);
    slots[fn] = furniture.filter((piece) => {
      const id = PIECE_CATALOG[piece.t]?.id;
      return id !== undefined && accepted.has(id);
    }).map((piece) => piece.pid).sort((a, b) => a - b);
    if (slots[fn][0] !== undefined) anchors[fn] = slots[fn][0];
  }
  return { anchors, slots };
}

function labelNear(field: HomesteadRoomField, p: Vec3): number {
  const frame: Frame = { origin: field.origin, x: field.xAxis, y: field.yAxis, z: field.zAxis };
  const lifted: Vec3 = [p[0] + frame.y[0] * 0.8, p[1] + frame.y[1] * 0.8, p[2] + frame.y[2] * 0.8];
  const direct = pointIndex(field, lifted);
  if (direct >= 0 && field.roomLabels[direct] > 0) return field.roomLabels[direct];
  if (direct < 0) return 0;
  const x0 = direct % field.nx;
  const yz = (direct - x0) / field.nx;
  const y0 = yz % field.ny;
  const z0 = (yz - y0) / field.ny;
  let best = 0, bestD = Infinity;
  for (let dz = -2; dz <= 2; dz++) for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
    const x = x0 + dx, y = y0 + dy, z = z0 + dz;
    if (x < 0 || y < 0 || z < 0 || x >= field.nx || y >= field.ny || z >= field.nz) continue;
    const label = field.roomLabels[idx(field, x, y, z)];
    const d = dx * dx + dy * dy + dz * dz;
    if (label > 0 && d < bestD) { best = label; bestD = d; }
  }
  return best;
}

/** Solve every socket-connected building in a beacon. */
export function solveHomesteadRooms(pieces: readonly PlacedPiece[]): HomesteadRoomSolution {
  const rooms: HomesteadRoom[] = [];
  const fields: HomesteadRoomField[] = [];
  const tooLargeComponents: number[] = [];
  const components = structuralComponents(pieces);

  for (let ci = 0; ci < components.length; ci++) {
    const component = components[ci];
    const frame = frameFor(component);
    const structuralObbs = component.flatMap((piece) => pieceWorldObbs(piece));
    const doorPieces = component.filter((p) => PIECE_CATALOG[p.t]?.id === DOOR_ID);
    const doorObbs = doorPieces.map(doorwayObb);
    const allBounds = [...structuralObbs, ...doorObbs];
    if (allBounds.length === 0) continue;

    const lo: Vec3 = [Infinity, Infinity, Infinity];
    const hi: Vec3 = [-Infinity, -Infinity, -Infinity];
    for (const b of allBounds) {
      const c = toFrame(frame, b.c);
      const e = obbExtentInFrame(frame, b);
      for (let axis = 0; axis < 3; axis++) {
        lo[axis] = Math.min(lo[axis], c[axis] - e[axis] - GRID_MARGIN);
        hi[axis] = Math.max(hi[axis], c[axis] + e[axis] + GRID_MARGIN);
      }
    }
    const min: Vec3 = lo.map((v) => Math.floor(v / ROOM_VOXEL_M) * ROOM_VOXEL_M) as Vec3;
    const nx = Math.max(1, Math.ceil((hi[0] - min[0]) / ROOM_VOXEL_M));
    const ny = Math.max(1, Math.ceil((hi[1] - min[1]) / ROOM_VOXEL_M));
    const nz = Math.max(1, Math.ceil((hi[2] - min[2]) / ROOM_VOXEL_M));
    const count = nx * ny * nz;
    if (count > ROOM_MAX_VOXELS) { tooLargeComponents.push(ci); continue; }

    const field: HomesteadRoomField = {
      component: ci,
      origin: frame.origin,
      xAxis: frame.x,
      yAxis: frame.y,
      zAxis: frame.z,
      min, nx, ny, nz,
      solid: new Uint8Array(count),
      doorways: new Uint8Array(count),
      interior: new Uint8Array(count),
      roomLabels: new Int32Array(count),
    };
    const doorMask = new Uint8Array(count);
    field.doorways = doorMask;
    for (const b of structuralObbs) rasterize(field, frame, b, false, doorMask);
    for (const b of doorObbs) rasterize(field, frame, b, true, doorMask);
    const exterior = floodExterior(field);
    const assigned = new Uint8Array(count);

    for (let seed = 0; seed < count; seed++) {
      if (field.solid[seed] || exterior[seed] || assigned[seed]) continue;
      const queue = [seed];
      const cells: number[] = [];
      assigned[seed] = 1;
      let hasDoor = false;
      while (queue.length > 0) {
        const here = queue.pop()!;
        cells.push(here);
        visitNeighbours(field, here, (next) => {
          if (doorMask[next]) hasDoor = true;
          if (field.solid[next] || exterior[next] || assigned[next]) return;
          assigned[next] = 1;
          queue.push(next);
        });
      }

      const colMin = new Int16Array(nx * nz); colMin.fill(32767);
      const colMax = new Int16Array(nx * nz); colMax.fill(-1);
      for (const i of cells) {
        const x = i % nx;
        const yz = (i - x) / nx;
        const y = yz % ny;
        const z = (yz - y) / ny;
        const c = x + nx * z;
        if (y < colMin[c]) colMin[c] = y;
        if (y > colMax[c]) colMax[c] = y;
      }
      let floorColumns = 0;
      for (let c = 0; c < colMin.length; c++) {
        if (colMax[c] >= 0 && colMax[c] - colMin[c] + 1 >= HEAD_CELLS) floorColumns++;
      }
      const floorAreaM2 = floorColumns * ROOM_VOXEL_M * ROOM_VOXEL_M;
      if (floorColumns > 0) for (const i of cells) field.interior[i] = 1;
      if (!hasDoor || floorAreaM2 < ROOM_MIN_FLOOR_M2) continue;

      const globalIndex = rooms.length;
      for (const i of cells) field.roomLabels[i] = globalIndex + 1;
      let sx = 0, sy = 0, sz = 0;
      for (const i of cells) {
        const x = i % nx;
        const yz = (i - x) / nx;
        const y = yz % ny;
        const z = (yz - y) / ny;
        const w = toWorld(frame, cellLocal(field, x, y, z));
        sx += w[0]; sy += w[1]; sz += w[2];
      }
      const n = cells.length || 1;
      const room: HomesteadRoom = {
        id: `room:${component[0].pid}:${globalIndex}`,
        component: ci,
        anchorPid: null,
        functionAnchors: {},
        functionSlots: emptyFunctionSlots(),
        centre: [sx / n, sy / n, sz / n],
        volumeM3: cells.length * ROOM_VOXEL_M * ROOM_VOXEL_M * ROOM_VOXEL_M,
        floorAreaM2,
        doorPids: doorPieces.map((p) => p.pid),
        furniturePids: [],
        functions: [],
        primary: "room",
        comfort: 0,
      };
      rooms.push(room);
    }
    fields.push(field);
  }

  // Assign furniture to the valid room whose air field contains its lifted
  // anchor. Furniture never participated in enclosure rasterization.
  const furniture = pieces.filter((p) => isFurniturePiece(p.t)).slice().sort((a, b) => a.pid - b.pid);
  const byRoom = new Map<number, PlacedPiece[]>();
  for (const piece of furniture) {
    let label = 0;
    for (const field of fields) {
      label = labelNear(field, piece.p);
      if (label > 0) break;
    }
    if (label <= 0) continue;
    const list = byRoom.get(label - 1) ?? [];
    list.push(piece);
    byRoom.set(label - 1, list);
  }
  for (let ri = 0; ri < rooms.length; ri++) {
    const room = rooms[ri];
    const placed = byRoom.get(ri) ?? [];
    const ids = new Set(placed.map((p) => PIECE_CATALOG[p.t]?.id).filter((id): id is string => Boolean(id)));
    room.furniturePids = placed.map((p) => p.pid);
    room.functions = classify(ids);
    room.primary = room.functions[0] ?? "room";
    room.comfort = furnitureCategories(ids);
    const stations = functionStations(room.functions, placed);
    room.functionAnchors = stations.anchors;
    room.functionSlots = stations.slots;
    const anchors = Object.values(room.functionAnchors);
    room.anchorPid = anchors.length > 0 ? Math.min(...anchors) : null;
    room.id = room.anchorPid === null ? room.id : `room:${room.anchorPid}`;
  }

  return { rooms, fields, tooLargeComponents };
}

export interface HomesteadWalkPath {
  points: Vec3[];
  cumulativeM: number[];
  totalM: number;
}

function walkCell(field: HomesteadRoomField, i: number): boolean {
  if (i < 0 || i >= field.solid.length) return false;
  const x = i % field.nx;
  const yz = (i - x) / field.nx;
  const y = yz % field.ny;
  const z = (yz - y) / field.ny;
  if (y < 1 || y + HEAD_CELLS >= field.ny) return false;
  // Doorway cells were solid only for enclosure recognition; a resident must
  // be able to use the opening that made the room valid.
  for (let h = 0; h < HEAD_CELLS; h++) {
    const at = idx(field, x, y + h, z);
    if (field.solid[at] && !field.doorways[at]) return false;
  }
  return field.solid[idx(field, x, y - 1, z)] !== 0;
}

function nearestWalkCell(field: HomesteadRoomField, point: Vec3): number {
  const frame: Frame = { origin: field.origin, x: field.xAxis, y: field.yAxis, z: field.zAxis };
  const lifted: Vec3 = [
    point[0] + frame.y[0] * 0.65,
    point[1] + frame.y[1] * 0.65,
    point[2] + frame.y[2] * 0.65,
  ];
  const direct = pointIndex(field, lifted);
  if (direct < 0) return -1;
  const x0 = direct % field.nx;
  const yz = (direct - x0) / field.nx;
  const y0 = yz % field.ny;
  const z0 = (yz - y0) / field.ny;
  let best = -1;
  let bestD = Infinity;
  for (let r = 0; r <= 6; r++) {
    for (let dz = -r; dz <= r; dz++) for (let dy = -2; dy <= 2; dy++) for (let dx = -r; dx <= r; dx++) {
      if (r > 0 && Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
      const x = x0 + dx, y = y0 + dy, z = z0 + dz;
      if (x < 0 || y < 0 || z < 0 || x >= field.nx || y >= field.ny || z >= field.nz) continue;
      const i = idx(field, x, y, z);
      if (!walkCell(field, i)) continue;
      const d = dx * dx + dz * dz + dy * dy * 2;
      if (d < bestD) { best = i; bestD = d; }
    }
    if (best >= 0) break;
  }
  return best;
}

function walkCellWorld(field: HomesteadRoomField, i: number): Vec3 {
  const x = i % field.nx;
  const yz = (i - x) / field.nx;
  const y = yz % field.ny;
  const z = (yz - y) / field.ny;
  return toWorld({ origin: field.origin, x: field.xAxis, y: field.yAxis, z: field.zAxis }, cellLocal(field, x, y, z));
}

/** Deterministic voxel A* through recognized homestead structure. Doorway
 * seals are reopened, headroom is enforced, and one-cell height changes are
 * allowed. Returns null when the anchors do not share a navigable component. */
export function homesteadWalkPath(solution: HomesteadRoomSolution, from: Vec3, to: Vec3): HomesteadWalkPath | null {
  let field: HomesteadRoomField | null = null;
  let start = -1, goal = -1;
  for (const candidate of solution.fields) {
    const s = nearestWalkCell(candidate, from);
    const g = nearestWalkCell(candidate, to);
    if (s >= 0 && g >= 0) { field = candidate; start = s; goal = g; break; }
  }
  if (!field) return null;
  const n = field.solid.length;
  const score = new Float64Array(n); score.fill(Infinity); score[start] = 0;
  const came = new Int32Array(n); came.fill(-1);
  const xyz = (i: number): [number, number, number] => {
    const x = i % field!.nx;
    const yz = (i - x) / field!.nx;
    const y = yz % field!.ny;
    return [x, y, (yz - y) / field!.ny];
  };
  const [gx, gy, gz] = xyz(goal);
  const heuristic = (i: number): number => {
    const [x, y, z] = xyz(i);
    return Math.hypot(gx - x, gy - y, gz - z) * ROOM_VOXEL_M;
  };
  const heapI: number[] = [];
  const heapF: number[] = [];
  const push = (i: number, f: number): void => {
    let at = heapI.length;
    heapI.push(i); heapF.push(f);
    while (at > 0) {
      const parent = (at - 1) >> 1;
      if (heapF[parent] <= heapF[at]) break;
      [heapI[parent], heapI[at]] = [heapI[at], heapI[parent]];
      [heapF[parent], heapF[at]] = [heapF[at], heapF[parent]];
      at = parent;
    }
  };
  const pop = (): [number, number] => {
    const i = heapI[0], f = heapF[0];
    const lastI = heapI.pop()!, lastF = heapF.pop()!;
    if (heapI.length > 0) {
      heapI[0] = lastI; heapF[0] = lastF;
      let at = 0;
      for (;;) {
        const left = at * 2 + 1, right = left + 1;
        let child = at;
        if (left < heapF.length && heapF[left] < heapF[child]) child = left;
        if (right < heapF.length && heapF[right] < heapF[child]) child = right;
        if (child === at) break;
        [heapI[child], heapI[at]] = [heapI[at], heapI[child]];
        [heapF[child], heapF[at]] = [heapF[at], heapF[child]];
        at = child;
      }
    }
    return [i, f];
  };
  push(start, heuristic(start));
  let expanded = 0;
  while (heapI.length > 0 && expanded++ < 100_000) {
    const [here, poppedF] = pop();
    if (poppedF > score[here] + heuristic(here) + 1e-9) continue; // stale improved entry
    if (here === goal) break;
    const [x, y, z] = xyz(here);
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = x + dx, nz = z + dz;
      if (nx < 0 || nz < 0 || nx >= field.nx || nz >= field.nz) continue;
      for (let dy = -1; dy <= 1; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= field.ny) continue;
        const next = idx(field, nx, ny, nz);
        if (!walkCell(field, next)) continue;
        const cost = score[here] + Math.hypot(dx, dy, dz) * ROOM_VOXEL_M;
        if (cost >= score[next]) continue;
        score[next] = cost;
        came[next] = here;
        push(next, cost + heuristic(next));
      }
    }
  }
  if (start !== goal && came[goal] < 0) return null;
  const cells: number[] = [];
  for (let at = goal; at >= 0; at = came[at]) {
    cells.push(at);
    if (at === start) break;
  }
  cells.reverse();
  const points: Vec3[] = [walkCellWorld(field, start)];
  let priorDir = "";
  for (let i = 1; i + 1 < cells.length; i++) {
    const [ax, ay, az] = xyz(cells[i - 1]);
    const [bx, by, bz] = xyz(cells[i]);
    const dir = `${bx - ax},${by - ay},${bz - az}`;
    if (dir !== priorDir && i > 1) points.push(walkCellWorld(field, cells[i - 1]));
    priorDir = dir;
  }
  if (cells.length > 1) points.push(walkCellWorld(field, cells[cells.length - 1]));
  const cumulativeM = [0];
  for (let i = 1; i < points.length; i++) {
    cumulativeM.push(cumulativeM[i - 1] + Math.hypot(
      points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1], points[i][2] - points[i - 1][2]));
  }
  return { points, cumulativeM, totalM: cumulativeM[cumulativeM.length - 1] };
}

/** Valid room containing a planet-local point. */
export function homesteadRoomAt(solution: HomesteadRoomSolution,
  x: number, y: number, z: number): HomesteadRoom | null {
  const p: Vec3 = [x, y, z];
  for (const field of solution.fields) {
    const i = pointIndex(field, p);
    if (i < 0) continue;
    const label = field.roomLabels[i];
    if (label > 0) return solution.rooms[label - 1] ?? null;
  }
  return null;
}

/** True for enclosed usable air, including a sealed-but-not-yet-door-valid room. */
export function homesteadIndoorsAt(solution: HomesteadRoomSolution,
  x: number, y: number, z: number): boolean {
  const p: Vec3 = [x, y, z];
  for (const field of solution.fields) {
    const i = pointIndex(field, p);
    if (i >= 0 && field.interior[i] !== 0) return true;
  }
  return false;
}
